import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/apiService';
import { csvProcessingService } from '../../services/CSVProcessingService';
import { ValidationSummary as ValidationSummaryType } from '../../types/reportTypes';
import ValidationSummary from './ValidationSummary';

// Type for file import specification
type UploadStatus = 'idle' | 'validating' | 'uploading' | 'mapping' | 'processing' | 'success' | 'error';

interface FileWithPreview extends File {
  preview?: string;
}

interface ValidationResult {
  isValid: boolean;
  errors: Array<{
    row: number;
    column: string;
    message: string;
  }>;
}

const CSVUploadPage: React.FC = () => {
  // Authentication context
  const { user: _ } = useAuth(); // Unused but kept for auth check
  const navigate = useNavigate();
  
  // File handling state
  const [salesFile, setSalesFile] = useState<FileWithPreview | null>(null);
  const [adSpendFile, setAdSpendFile] = useState<FileWithPreview | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // CSV validation state
  const [salesValidation, setSalesValidation] = useState<ValidationResult>({ isValid: false, errors: [] });
  const [adSpendValidation, setAdSpendValidation] = useState<ValidationResult>({ isValid: false, errors: [] });
  
  // Session tracking
  const [salesSessionId, setSalesSessionId] = useState<string | null>(null);
  const [adSpendSessionId, setAdSpendSessionId] = useState<string | null>(null);
  
  // Validation results
  const [salesValidationSummary, setSalesValidationSummary] = useState<ValidationSummaryType | null>(null);
  const [adSpendValidationSummary, setAdSpendValidationSummary] = useState<ValidationSummaryType | null>(null);
  const [showDetailedValidation, setShowDetailedValidation] = useState(false);
  
  // Column mapping state
  const [salesColumns, setSalesColumns] = useState<string[]>([]);
  const [adSpendColumns, setAdSpendColumns] = useState<string[]>([]);
  const [salesColumnMappings, setSalesColumnMappings] = useState<Record<string, string>>({});
  const [adSpendColumnMappings, setAdSpendColumnMappings] = useState<Record<string, string>>({});
  const [showMappingUI, setShowMappingUI] = useState(false);
  
  // Report information
  const [reportTitle, setReportTitle] = useState('');
  const currentDate = new Date();
  const [reportMonth, setReportMonth] = useState(currentDate.getMonth() + 1);
  const [reportYear, setReportYear] = useState(currentDate.getFullYear());
  const [reportType, setReportType] = useState<'monthly' | 'quarterly' | 'annual'>('monthly');
  
  // Refs for file inputs
  const salesFileInput = useRef<HTMLInputElement>(null);
  const adSpendFileInput = useRef<HTMLInputElement>(null);
  
  // Reset all form state
  const resetForm = () => {
    setSalesFile(null);
    setAdSpendFile(null);
    setUploadStatus('idle');
    setUploadProgress(0);
    setErrorMessage(null);
    setSalesValidation({ isValid: false, errors: [] });
    setAdSpendValidation({ isValid: false, errors: [] });
    setSalesSessionId(null);
    setAdSpendSessionId(null);
    setSalesValidationSummary(null);
    setAdSpendValidationSummary(null);
    setShowDetailedValidation(false);
    setSalesColumns([]);
    setAdSpendColumns([]);
    setSalesColumnMappings({});
    setAdSpendColumnMappings({});
    setShowMappingUI(false);
    
    // Reset file inputs
    if (salesFileInput.current) salesFileInput.current.value = '';
    if (adSpendFileInput.current) adSpendFileInput.current.value = '';
  };
  
  // Handle file selection for sales data
  const handleSalesFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Check file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setErrorMessage('Please upload a CSV file');
        return;
      }
      
      // Set file state
      setSalesFile(file);
      setErrorMessage(null);
      setUploadStatus('validating');
      
      try {
        // Validate CSV structure using local service
        const { data, errors } = await csvProcessingService.parseSalesCSV(file);
        
        // Extract column headers
        if (data.length > 0) {
          const headers = Object.keys(data[0]).filter(key => key.trim() !== '');
          setSalesColumns(headers);
          
          // Create initial mapping (identity mapping)
          const initialMapping: Record<string, string> = {};
          headers.forEach(header => {
            initialMapping[header] = header;
          });
          setSalesColumnMappings(initialMapping);
        }
        
        setSalesValidation({
          isValid: errors.length === 0,
          errors
        });
        
        if (errors.length > 0) {
          setUploadStatus('error');
        }
      } catch (error) {
        setErrorMessage('Failed to parse sales CSV file');
        setUploadStatus('error');
      }
    }
  };
  
  // Handle file selection for ad spend data
  const handleAdSpendFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Check file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setErrorMessage('Please upload a CSV file');
        return;
      }
      
      // Set file state
      setAdSpendFile(file);
      setErrorMessage(null);
      setUploadStatus('validating');
      
      try {
        // Validate CSV structure using local service
        const { data, errors } = await csvProcessingService.parseAdSpendCSV(file);
        
        // Extract column headers
        if (data.length > 0) {
          const headers = Object.keys(data[0]).filter(key => key.trim() !== '');
          setAdSpendColumns(headers);
          
          // Create initial mapping (identity mapping)
          const initialMapping: Record<string, string> = {};
          headers.forEach(header => {
            initialMapping[header] = header;
          });
          setAdSpendColumnMappings(initialMapping);
        }
        
        setAdSpendValidation({
          isValid: errors.length === 0,
          errors
        });
        
        if (errors.length > 0) {
          setUploadStatus('error');
        }
      } catch (error) {
        setErrorMessage('Failed to parse ad spend CSV file');
        setUploadStatus('error');
      }
    }
  };
  
  // Handle file upload
  const handleUpload = async () => {
    setUploadStatus('uploading');
    setUploadProgress(10);
    setErrorMessage(null);
    
    try {
      // Log info for debugging
      console.log('Initiating file upload process');
      console.log('Sales file:', salesFile?.name);
      console.log('Ad spend file:', adSpendFile?.name);
      
      // Make sure we have a token
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.log('No auth token found, creating test token');
        localStorage.setItem('authToken', 'test-token-for-local-development');
      }
      
      // Use temporary variables to track session IDs
      let tempSalesSessionId = null;
      let tempAdSpendSessionId = null;
      
      // Upload sales file
      if (salesFile) {
        console.log('Uploading sales file:', salesFile.name);
        try {
          const salesResult = await apiService.uploadCSV(salesFile, 'sales_data');
          console.log('Sales upload result:', salesResult);
          
          // Make sure we got an ID back
          if (salesResult && salesResult.id) {
            tempSalesSessionId = salesResult.id;
            setSalesSessionId(salesResult.id);
            console.log('Successfully set sales session ID:', salesResult.id);
          } else {
            console.error('Sales upload response missing ID:', salesResult);
          }
          setUploadProgress(40);
        } catch (salesError) {
          console.error('Sales file upload error:', salesError);
          // Continue with ad spend file even if sales file fails
          setErrorMessage(`Sales file error: ${salesError instanceof Error ? salesError.message : 'Unknown error'}`);
        }
      }
      
      // Upload ad spend file
      if (adSpendFile) {
        console.log('Uploading ad spend file:', adSpendFile.name);
        try {
          const adSpendResult = await apiService.uploadCSV(adSpendFile, 'ad_spend_data');
          console.log('Ad spend upload result:', adSpendResult);
          
          // Make sure we got an ID back
          if (adSpendResult && adSpendResult.id) {
            tempAdSpendSessionId = adSpendResult.id;
            setAdSpendSessionId(adSpendResult.id);
            console.log('Successfully set ad spend session ID:', adSpendResult.id);
          } else {
            console.error('Ad spend upload response missing ID:', adSpendResult);
          }
          setUploadProgress(70);
        } catch (adSpendError) {
          console.error('Ad spend file upload error:', adSpendError);
          setErrorMessage(`Ad spend file error: ${adSpendError instanceof Error ? adSpendError.message : 'Unknown error'}`);
        }
      }
      
      // Check if we have at least one session ID
      if (tempSalesSessionId || tempAdSpendSessionId) {
        console.log('Upload completed with sessions:', { 
          salesSessionId: tempSalesSessionId, 
          adSpendSessionId: tempAdSpendSessionId 
        });
        setUploadProgress(100);
        setShowMappingUI(true);
        setUploadStatus('mapping');
      } else {
        console.error('No valid sessions created');
        setErrorMessage('Failed to create import sessions. Please check browser console for details.');
        setUploadStatus('error');
      }
    } catch (error) {
      console.error('Overall upload error:', error);
      setErrorMessage(`Upload error: ${error instanceof Error ? error.message : 'Failed to upload files. See console for details.'}`);
      setUploadStatus('error');
    }
  };
  
  // Handle column mapping change for sales data
  const handleSalesMappingChange = (originalColumn: string, mappedColumn: string) => {
    setSalesColumnMappings(prev => ({
      ...prev,
      [originalColumn]: mappedColumn
    }));
  };
  
  // Handle column mapping change for ad spend data
  const handleAdSpendMappingChange = (originalColumn: string, mappedColumn: string) => {
    setAdSpendColumnMappings(prev => ({
      ...prev,
      [originalColumn]: mappedColumn
    }));
  };
  
  // Process uploaded files with column mappings
  const handleProcessFiles = async () => {
    setUploadStatus('processing');
    setUploadProgress(0);
    setShowDetailedValidation(false);
    
    try {
      // Track if we have any successful processing
      let hasSuccessfulProcessing = false;
      let salesValidationResult = null;
      let adSpendValidationResult = null;
      
      // Process sales data
      if (salesSessionId) {
        try {
          console.log('Processing sales data with session ID:', salesSessionId);
          console.log('Sales column mappings:', salesColumnMappings);
          
          const salesResult = await apiService.processCSV(salesSessionId, salesColumnMappings);
          salesValidationResult = salesResult;
          
          if (salesResult.validation_summary) {
            setSalesValidationSummary(salesResult.validation_summary);
            // Mark as successful even if there are issues - at least the API call worked
            hasSuccessfulProcessing = true;
          }
          
          setUploadProgress(40);
        } catch (error) {
          const salesError = error as Error;
          console.error('Error processing sales data:', salesError);
          // Continue with ad spend processing even if sales processing fails
          setSalesValidationSummary({
            total_rows: 0,
            processed_rows: 0,
            skipped_rows: 0,
            has_issues: true,
            issues: [{ rowIndex: 0, issues: ['Failed to process sales data. Error: ' + (salesError.message || 'Unknown error')] }]
          });
        }
      }
      
      // Process ad spend data
      if (adSpendSessionId) {
        try {
          console.log('Processing ad spend data with session ID:', adSpendSessionId);
          console.log('Ad spend column mappings:', adSpendColumnMappings);
          
          const adSpendResult = await apiService.processCSV(adSpendSessionId, adSpendColumnMappings);
          adSpendValidationResult = adSpendResult;
          
          if (adSpendResult.validation_summary) {
            setAdSpendValidationSummary(adSpendResult.validation_summary);
            // Mark as successful even if there are issues - at least the API call worked
            hasSuccessfulProcessing = true;
          }
          
          setUploadProgress(70);
        } catch (error) {
          const adSpendError = error as Error;
          console.error('Error processing ad spend data:', adSpendError);
          // Set fallback validation summary for UI display
          setAdSpendValidationSummary({
            total_rows: 0,
            processed_rows: 0,
            skipped_rows: 0,
            has_issues: true,
            issues: [{ rowIndex: 0, issues: ['Failed to process ad spend data. Error: ' + (adSpendError.message || 'Unknown error')] }]
          });
        }
      }
      
      // Always show validation results now, especially if we had errors
      setShowDetailedValidation(true);
      setUploadProgress(85);
      
      // Check for validation issues
      const hasSalesIssues = salesValidationResult?.success === false;
      const hasAdSpendIssues = adSpendValidationResult?.success === false;
      
      // Show detailed results even if there are issues
      console.log('Validation results:', {
        salesValidation: salesValidationResult,
        adSpendValidation: adSpendValidationResult,
        hasSuccessfulProcessing
      });
      
      // Skip report generation if processing failed completely
      if (!hasSuccessfulProcessing || (hasSalesIssues && hasAdSpendIssues)) {
        console.error('No data was successfully processed, skipping report generation');
        setErrorMessage('Failed to process files. Please check file format or check server logs for details.');
        setUploadStatus('error');
        return;
      }
      
      try {
        // Generate report with whatever data was successfully processed
        const reportParams = {
          title: reportTitle || `Marketing Report - ${reportMonth}/${reportYear}`,
          period: { month: reportMonth, year: reportYear },
          salesDataImportIds: salesSessionId && salesValidationResult?.success !== false ? [salesSessionId] : [],
          adSpendDataImportIds: adSpendSessionId && adSpendValidationResult?.success !== false ? [adSpendSessionId] : []
        };

        const reportResult = await apiService.generateReportWithParams(reportParams);
        
        setUploadProgress(100);
        setUploadStatus('success');
        
        // Navigate to the new report with delay if showing validation results
        setTimeout(() => {
          navigate(`/reports/${reportResult.id}`);
        }, 3000); // Always use a delay to show validation results
      } catch (reportError) {
        console.error('Report generation error:', reportError);
        setErrorMessage('Failed to generate report: ' + (reportError instanceof Error ? reportError.message : 'Unknown error'));
        setUploadStatus('error');
      }
    } catch (error) {
      console.error('Overall processing error:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to process files');
      setUploadStatus('error');
    }
  };
  
  // Standard mapping options for sales data and ad spend data
  const standardSalesColumns = [
    'Lead source',
    'Trade',
    'Job Info',
    'Contract Total',
    'Gross Margin %',
    'Gross Profit',
    'CP',
    'Sales Commission',
    'Sales Manager Fee',
    'Company OH'
  ];
  
  const standardAdSpendColumns = [
    'Lead Source',
    'Spend'
  ];
  
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            Upload Marketing Data
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Upload your sales and ad spend data to generate a marketing performance report.
          </p>
        </div>
        
        {/* Error Display */}
        {errorMessage && (
          <div className="bg-red-50 border border-red-400 text-red-700 p-4 rounded mb-8">
            <h2 className="font-bold text-lg mb-2">Error</h2>
            <p>{errorMessage}</p>
            <button 
              className="mt-2 text-red-700 underline"
              onClick={() => setErrorMessage(null)}
            >
              Dismiss
            </button>
          </div>
        )}
        
        {/* Success Message */}
        {uploadStatus === 'success' && (
          <div className="bg-green-50 border border-green-400 text-green-700 p-4 rounded mb-8">
            <h2 className="font-bold text-lg mb-2">✅ Files Processed Successfully</h2>
            <p>Your files have been processed and the report is being generated.</p>
            <p>You will be redirected to the report page shortly.</p>
          </div>
        )}
        
        {/* File Upload Section */}
        {(uploadStatus === 'idle' || uploadStatus === 'validating' || uploadStatus === 'error') && (
          <div className="space-y-6">
            {/* Report Information */}
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h2 className="text-xl font-bold mb-4">Report Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Report Title
                  </label>
                  <input
                    type="text"
                    value={reportTitle}
                    onChange={(e) => setReportTitle(e.target.value)}
                    placeholder="Marketing Report"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Report Type
                  </label>
                  <select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value as 'monthly' | 'quarterly' | 'annual')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Month
                  </label>
                  <select
                    value={reportMonth}
                    onChange={(e) => setReportMonth(parseInt(e.target.value))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="1">January</option>
                    <option value="2">February</option>
                    <option value="3">March</option>
                    <option value="4">April</option>
                    <option value="5">May</option>
                    <option value="6">June</option>
                    <option value="7">July</option>
                    <option value="8">August</option>
                    <option value="9">September</option>
                    <option value="10">October</option>
                    <option value="11">November</option>
                    <option value="12">December</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Year
                  </label>
                  <input
                    type="number"
                    value={reportYear}
                    onChange={(e) => setReportYear(parseInt(e.target.value))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
            
            {/* Sales Data Upload */}
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h2 className="text-xl font-bold mb-4">Sales Data</h2>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  ref={salesFileInput}
                  onChange={handleSalesFileChange}
                  accept=".csv"
                  className="hidden"
                  id="sales-file-input"
                />
                {!salesFile ? (
                  <div>
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      ></path>
                    </svg>
                    <p className="mt-1 text-sm text-gray-600">
                      Drag and drop or click to upload your sales data CSV file
                    </p>
                    <button
                      onClick={() => salesFileInput.current?.click()}
                      className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Select File
                    </button>
                    <a
                      href="/sample-sales-data.csv"
                      download
                      className="ml-2 text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Download Sample
                    </a>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-center">
                      <svg
                        className="h-8 w-8 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                      </svg>
                      <span className="ml-2 text-sm font-medium text-gray-900">
                        {salesFile.name}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setSalesFile(null);
                        if (salesFileInput.current) salesFileInput.current.value = '';
                      }}
                      className="mt-2 text-sm text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
              
              {/* Sales Validation Errors */}
              {salesValidation.errors.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-red-600 font-bold">Validation Errors</h3>
                  <ul className="list-disc pl-5 text-sm text-red-600">
                    {salesValidation.errors.slice(0, 5).map((error, index) => (
                      <li key={index}>
                        {error.row === 0
                          ? error.message
                          : `Row ${error.row}: ${error.message}`}
                      </li>
                    ))}
                    {salesValidation.errors.length > 5 && (
                      <li>
                        ...and {salesValidation.errors.length - 5} more errors
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
            
            {/* Ad Spend Data Upload */}
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h2 className="text-xl font-bold mb-4">Ad Spend Data</h2>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  type="file"
                  ref={adSpendFileInput}
                  onChange={handleAdSpendFileChange}
                  accept=".csv"
                  className="hidden"
                  id="adspend-file-input"
                />
                {!adSpendFile ? (
                  <div>
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      ></path>
                    </svg>
                    <p className="mt-1 text-sm text-gray-600">
                      Drag and drop or click to upload your ad spend data CSV file
                    </p>
                    <button
                      onClick={() => adSpendFileInput.current?.click()}
                      className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                    >
                      Select File
                    </button>
                    <a
                      href="/sample-adspend-data.csv"
                      download
                      className="ml-2 text-green-600 hover:text-green-800 text-sm"
                    >
                      Download Sample
                    </a>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-center">
                      <svg
                        className="h-8 w-8 text-green-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                      </svg>
                      <span className="ml-2 text-sm font-medium text-gray-900">
                        {adSpendFile.name}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setAdSpendFile(null);
                        if (adSpendFileInput.current) adSpendFileInput.current.value = '';
                      }}
                      className="mt-2 text-sm text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>
              
              {/* Ad Spend Validation Errors */}
              {adSpendValidation.errors.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-red-600 font-bold">Validation Errors</h3>
                  <ul className="list-disc pl-5 text-sm text-red-600">
                    {adSpendValidation.errors.slice(0, 5).map((error, index) => (
                      <li key={index}>
                        {error.row === 0
                          ? error.message
                          : `Row ${error.row}: ${error.message}`}
                      </li>
                    ))}
                    {adSpendValidation.errors.length > 5 && (
                      <li>
                        ...and {adSpendValidation.errors.length - 5} more errors
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>
            
            {/* Upload Button */}
            <div className="flex justify-end">
              <button
                onClick={handleUpload}
                disabled={
                  !salesFile ||
                  !adSpendFile ||
                  salesValidation.errors.length > 0 ||
                  adSpendValidation.errors.length > 0
                }
                className={`px-4 py-2 rounded ${
                  !salesFile || !adSpendFile || salesValidation.errors.length > 0 || adSpendValidation.errors.length > 0
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                Upload Files
              </button>
            </div>
          </div>
        )}
        
        {/* Column Mapping UI */}
        {showMappingUI && (uploadStatus === 'mapping' || uploadStatus === 'processing') && (
          <div className="space-y-6">
            <h2 className="text-xl font-bold mb-4">Map Columns</h2>
            <p className="text-gray-600 mb-4">
              Match your CSV columns to our standard column names for proper data processing.
            </p>
            
            {/* Progress Indicator */}
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
              <div
                className="bg-blue-600 h-2.5 rounded-full"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            
            {/* Display Validation Results */}
            {showDetailedValidation && (
              <div className="space-y-4">
                <h3 className="text-lg font-bold">Processing Results</h3>
                
                {salesValidationSummary && (
                  <ValidationSummary 
                    summary={salesValidationSummary} 
                    importType="sales_data" 
                  />
                )}
                
                {adSpendValidationSummary && (
                  <ValidationSummary 
                    summary={adSpendValidationSummary} 
                    importType="ad_spend_data" 
                  />
                )}
                
                {(salesValidationSummary?.has_issues || adSpendValidationSummary?.has_issues) && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-yellow-700">
                          Some rows were skipped or processed with warnings. The report will be generated using only valid data.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Sales Data Column Mapping */}
            {salesColumns.length > 0 && (
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="font-bold mb-4">Sales Data Columns</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {salesColumns.map((column) => (
                    <div key={column} className="flex flex-col">
                      <label className="block text-sm font-medium text-gray-700">
                        {column}
                      </label>
                      <select
                        value={salesColumnMappings[column] || ''}
                        onChange={(e) => handleSalesMappingChange(column, e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="">-- Select mapped column --</option>
                        {standardSalesColumns.map((stdCol) => (
                          <option key={stdCol} value={stdCol}>
                            {stdCol}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Ad Spend Data Column Mapping */}
            {adSpendColumns.length > 0 && (
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="font-bold mb-4">Ad Spend Data Columns</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {adSpendColumns.map((column) => (
                    <div key={column} className="flex flex-col">
                      <label className="block text-sm font-medium text-gray-700">
                        {column}
                      </label>
                      <select
                        value={adSpendColumnMappings[column] || ''}
                        onChange={(e) => handleAdSpendMappingChange(column, e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        <option value="">-- Select mapped column --</option>
                        {standardAdSpendColumns.map((stdCol) => (
                          <option key={stdCol} value={stdCol}>
                            {stdCol}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Process Button */}
            <div className="flex justify-end">
              <button
                onClick={resetForm}
                className="mr-4 px-4 py-2 bg-gray-300 hover:bg-gray-400 rounded"
              >
                Cancel
              </button>
              
              {showDetailedValidation && uploadStatus === 'processing' ? (
                <button
                  onClick={() => navigate(`/reports`)}
                  className="px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white"
                >
                  View Report
                </button>
              ) : (
                <button
                  onClick={handleProcessFiles}
                  disabled={uploadStatus === 'processing' && !showDetailedValidation}
                  className={`px-4 py-2 rounded ${
                    (uploadStatus === 'processing' && !showDetailedValidation)
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                >
                  {uploadStatus === 'processing' && !showDetailedValidation ? 'Processing...' : 'Process Files'}
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Back to Dashboard Button */}
        <div className="text-center mt-8">
          <Link
            to="/dashboard"
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded inline-flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
            </svg>
            Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CSVUploadPage;